import React from "react";
import PropTypes from "prop-types";
import { makeStyles } from "@material-ui/core/styles";
import Button from "@material-ui/core/Button";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableContainer from "@material-ui/core/TableContainer";
import TableHead from "@material-ui/core/TableHead";
import TablePagination from "@material-ui/core/TablePagination";
import TableRow from "@material-ui/core/TableRow";
import TableSortLabel from "@material-ui/core/TableSortLabel";
import Typography from "@material-ui/core/Typography";
import Chip from "@material-ui/core/Chip";
import Tooltip from "@material-ui/core/Tooltip";
import Paper from "@material-ui/core/Paper";
import IconButton from "@material-ui/core/IconButton";
import MenuItem from "@material-ui/core/MenuItem";
import MoreVertIcon from "@material-ui/icons/MoreVert";
import Menu from "@material-ui/core/Menu";
import PhoneIcon from "@material-ui/icons/Phone";
import FingerprintIcon from "@material-ui/icons/Fingerprint";
import NotificationsActiveIcon from "@material-ui/icons/NotificationsActive";

import resolveUserSteamId from "./resolveUserSteamId.js";
import {
  buildCsrepUrl,
  buildVacBanUrl,
  CSREP_ICON_URL,
  VAC_BAN_ICON_URL,
} from "./steamExternalLinks.js";
import {
  DEFAULT_USER_MANAGEMENT_FILTERS,
  rowMatchesFilters,
} from "./userManagementFilters.js";
import {
  getMembershipTier,
  getMembershipTierDisplay,
} from "./getMembershipTierDisplay.js";
import UserManagementFilterBar from "./UserManagementFilterBar.js";
import { UserCountryCell } from "./CountryBadge.js";

function formatEmailDisplay(email) {
  if (!email) return { label: null, isSynthetic: false };
  if (email.endsWith("@steam.ouro.local")) {
    return { label: "Synthetic — Steam", isSynthetic: true };
  }
  if (email.endsWith("@playsafe.ouro.local")) {
    return { label: "Synthetic — PlaysafeID", isSynthetic: true };
  }
  return { label: email, isSynthetic: false };
}

const JOINED_AT_RELATIVE_FORMATTER = new Intl.RelativeTimeFormat(undefined, {
  numeric: "always",
  style: "short",
});
const JOINED_AT_TOOLTIP_FORMATTER = new Intl.DateTimeFormat(undefined, {
  year: "numeric",
  month: "long",
  day: "numeric",
  hour: "2-digit",
  minute: "2-digit",
});

function parseUserJoinedAt(value) {
  if (value == null) {
    return null;
  }

  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }

  if (typeof value === "number" && Number.isFinite(value)) {
    const millis = value > 1000000000000 ? value : value * 1000;
    const parsedDate = new Date(millis);
    return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
  }

  if (typeof value === "string") {
    const trimmedValue = value.trim();
    if (!trimmedValue) {
      return null;
    }

    const numericValue = Number(trimmedValue);
    if (Number.isFinite(numericValue)) {
      return parseUserJoinedAt(numericValue);
    }

    const parsedDate = new Date(trimmedValue);
    return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
  }

  return null;
}

function getDateMillis(value) {
  const parsedDate = parseUserJoinedAt(value);
  return parsedDate ? parsedDate.getTime() : 0;
}

function getJoinedAtMillis(row) {
  return getDateMillis(row?.createdAt);
}

function formatRelativeTime(millis, unavailableLabel) {
  if (!millis) {
    return unavailableLabel;
  }

  const nowMillis = Date.now();
  const diffMillis = millis - nowMillis;
  const absoluteSeconds = Math.abs(diffMillis) / 1000;

  if (absoluteSeconds < 60) {
    return "just now";
  }

  const ranges = [
    { unit: "year", seconds: 31536000 },
    { unit: "month", seconds: 2592000 },
    { unit: "week", seconds: 604800 },
    { unit: "day", seconds: 86400 },
    { unit: "hour", seconds: 3600 },
    { unit: "minute", seconds: 60 },
  ];

  const targetRange =
    ranges.find((range) => absoluteSeconds >= range.seconds) ||
    ranges[ranges.length - 1];
  const unitDelta = Math.round(diffMillis / 1000 / targetRange.seconds);

  return JOINED_AT_RELATIVE_FORMATTER.format(unitDelta, targetRange.unit);
}

function formatAbsoluteTime(millis, unavailableLabel) {
  if (!millis) {
    return unavailableLabel;
  }

  return JOINED_AT_TOOLTIP_FORMATTER.format(new Date(millis));
}

function getSortableValue(row, orderBy) {
  if (orderBy === "name") {
    return getJoinedAtMillis(row);
  }

  if (orderBy === "lastVisitedAt") {
    return getDateMillis(row?.lastVisitedAt);
  }

  if (orderBy === "status") {
    return row?.accountStatus || "";
  }

  if (orderBy === "membership") {
    return getMembershipTier(row);
  }

  if (orderBy === "identityRisk") {
    const level = row?.identityRisk?.level || "none";
    const ranking = {
      high: 5,
      medium: 4,
      pending: 3,
      scan_failed: 2,
      clear: 1,
      none: 0,
    };
    return ranking[level] || 0;
  }

  if (orderBy === "phoneNumber") {
    const phone =
      typeof row?.phoneNumber === "string" ? row.phoneNumber.trim().toLowerCase() : "";
    return phone;
  }

  if (orderBy === "phoneVerification") {
    const st = row?.phoneVerification?.status || "";
    const biometricVerified = row?.kyc?.badgeEligible === true;
    const ranking = {
      verified: 4,
      code_sent: 3,
      expired: 2,
      not_started: 1,
      "": 0,
    };
    return (ranking[st] ?? 0) + (biometricVerified ? 0.5 : 0);
  }

  if (orderBy === "profileTrustFactor") {
    return Number(row?.profileTrustFactor) || 0;
  }

  const value = row?.[orderBy];
  return typeof value === "string" ? value.toLowerCase() : value ?? "";
}

function getMembershipChipClass(classes, tier) {
  if (tier === "pro") {
    return classes.membershipPro;
  }

  if (tier === "premium") {
    return classes.membershipPremium;
  }

  if (tier === "unknown") {
    return classes.membershipUnknown;
  }

  return classes.membershipFree;
}

function getIdentityRiskLabel(identityRisk) {
  if (!identityRisk) {
    return "No scan";
  }

  if (identityRisk.level === "high") {
    return `${identityRisk.exactMatchCount || 0} exact`;
  }

  if (identityRisk.level === "medium") {
    return `${identityRisk.softMatchCount || 0} soft`;
  }

  if (identityRisk.level === "pending") {
    return "Scanning";
  }

  if (identityRisk.level === "scan_failed") {
    return "Failed";
  }

  return "Clear";
}

function getIdentityRiskChipClass(classes, identityRisk) {
  const level = identityRisk?.level || "none";
  if (level === "high") {
    return classes.identityRiskHigh;
  }
  if (level === "medium") {
    return classes.identityRiskMedium;
  }
  if (level === "pending") {
    return classes.identityRiskPending;
  }
  if (level === "scan_failed") {
    return classes.identityRiskFailed;
  }
  return classes.identityRiskClear;
}

function descendingComparator(a, b, orderBy) {
  const aValue = getSortableValue(a, orderBy);
  const bValue = getSortableValue(b, orderBy);

  if (bValue < aValue) {
    return -1;
  }

  if (bValue > aValue) {
    return 1;
  }

  return 0;
}

function getComparator(order, orderBy) {
  return order === "desc"
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function stableSort(array, comparator) {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  return stabilizedThis.map((el) => el[0]);
}

function getClampedPage(page, rowCount, rowsPerPage) {
  const maxPage = Math.max(0, Math.ceil(rowCount / rowsPerPage) - 1);
  return Math.min(Math.max(page, 0), maxPage);
}

function hasVerifiedPhone(row) {
  return row?.phoneVerification?.status === "verified";
}

function hasValidPhoneNumber(row) {
  const phone =
    typeof row?.phoneNumber === "string" ? row.phoneNumber.trim() : "";
  if (!phone.startsWith("+")) {
    return false;
  }

  if (!/^\+[0-9().\s-]+$/.test(phone)) {
    return false;
  }

  const digits = phone.slice(1).replace(/\D/g, "");
  if (digits.length < 8 || digits.length > 15) {
    return false;
  }

  return true;
}

function hasProvidedPhoneNumber(row) {
  const phone =
    typeof row?.phoneNumber === "string" ? row.phoneNumber.trim() : "";
  return phone.length > 0;
}

function hasSendPhoneVerificationBlockedAccountStatus(row) {
  return (
    row?.accountStatus === "banned" ||
    row?.accountStatus === "noteligible"
  );
}

function isSetAsVerifiedDisabled(row) {
  if (!row) {
    return true;
  }

  return !["pending_review", "noteligible"].includes(row.accountStatus);
}

function isApprovePhoneVerificationDisabled(row) {
  if (!row) {
    return true;
  }

  if (
    row?.accountStatus !== "pending_review" &&
    row?.accountStatus !== "verified" &&
    row?.accountStatus !== "active"
  ) {
    return true;
  }

  if (!hasValidPhoneNumber(row)) {
    return true;
  }

  if (hasVerifiedPhone(row)) {
    return true;
  }

  if (row?.phoneVerification?.status === "phone_verification_ready") {
    return true;
  }

  if (row?.phoneVerification?.status === "code_sent") {
    return true;
  }

  if (hasSendPhoneVerificationBlockedAccountStatus(row)) {
    return true;
  }

  return false;
}

function getPhoneVerificationColumnLabel(row) {
  const st = row?.phoneVerification?.status;
  if (st === "verified") {
    return "Verified";
  }
  if (st === "code_sent") {
    return "Code sent";
  }
  if (st === "expired") {
    return "Expired";
  }
  return "Not started";
}

function getPhoneVerificationColumnChipClass(classes, row) {
  const st = row?.phoneVerification?.status;
  if (st === "verified") {
    return classes.phoneVerificationVerified;
  }
  if (st === "code_sent") {
    return classes.phoneVerificationPending;
  }
  if (st === "expired") {
    return classes.phoneVerificationExpired;
  }
  return classes.phoneVerificationIdle;
}

function getBiometricVerificationColumnLabel(row) {
  const st = row?.kyc?.status;
  if (row?.kyc?.badgeEligible === true || st === "approved") {
    return "Verified";
  }

  if (st === "in_progress") {
    return "In progress";
  }
  if (st === "in_review") {
    return "In review";
  }
  if (st === "declined") {
    return "Declined";
  }
  if (st === "abandoned") {
    return "Abandoned";
  }
  if (st === "expired") {
    return "Expired";
  }

  return "Not started";
}

function getBiometricVerificationColumnChipClass(classes, row) {
  const st = row?.kyc?.status;
  if (row?.kyc?.badgeEligible === true || st === "approved") {
    return classes.phoneVerificationVerified;
  }
  if (st === "in_progress" || st === "in_review") {
    return classes.phoneVerificationPending;
  }
  if (st === "declined" || st === "abandoned" || st === "expired") {
    return classes.phoneVerificationExpired;
  }
  return classes.phoneVerificationIdle;
}

function getDesktopNotificationColumnLabel(row) {
  return row?.hasDesktopPushSubscription ? "Desktop alerts" : "No desktop alerts";
}

function getDesktopNotificationColumnChipClass(classes, row) {
  return row?.hasDesktopPushSubscription
    ? classes.phoneVerificationVerified
    : classes.phoneVerificationIdle;
}

const headCells = [
  {
    id: "name",
    numeric: false,
    width: 220,
    disablePadding: false,
    label: "Player Name",
  },
  {
    id: "nickname",
    width: 180,
    numeric: false,
    disablePadding: true,
    label: "Nickname",
  },
  {
    id: "status",
    width: 128,
    numeric: false,
    disablePadding: true,
    align: "center",
    label: "Status",
  },
  {
    id: "elo",
    align: "center",
    width: 80,
    numeric: false,
    disablePadding: true,
    label: "ELO",
  },
  {
    id: "country",
    align: "center",
    width: 112,
    numeric: false,
    disablePadding: true,
    label: "Country",
  },
  {
    id: "age",
    align: "center",
    numeric: false,
    disablePadding: true,
    label: "Age",
  },
  {
    id: "steamProfileUrl",
    numeric: false,
    align: "center",
    disablePadding: true,
    label: "Steam Profile",
  },
  {
    id: "faceitProfileUrl",
    numeric: false,
    align: "center",
    disablePadding: true,
    label: "FaceIT Profile",
  },
  {
    id: "phoneNumber",
    numeric: false,
    width: 150,
    align: "center",
    disablePadding: true,
    label: "Phone",
  },
  {
    id: "phoneVerification",
    numeric: false,
    width: 150,
    align: "center",
    disablePadding: true,
    label: "Verification",
  },
  {
    id: "profileTrustFactor",
    numeric: false,
    width: 110,
    align: "center",
    disablePadding: true,
    label: "Trust",
  },
  {
    id: "actions",
    numeric: false,
    align: "center",
    disablePadding: true,
    label: "Actions",
  },
];

function ProfileIconLink({ href, src, label, iconClassName, linkClassName }) {
  if (!href) {
    return (
      <img
        className={iconClassName}
        src={src}
        alt={`${label} — not linked`}
        style={{ opacity: 0.25, filter: "grayscale(100%)", cursor: "default" }}
      />
    );
  }

  return (
    <Tooltip arrow title={`Open ${label}`} placement="top">
      <a
        className={linkClassName}
        href={href}
        target="_blank"
        rel="noreferrer"
      >
        <img className={iconClassName} src={src} alt={label} />
      </a>
    </Tooltip>
  );
}

function EnhancedTableHead(props) {
  const { classes, order, orderBy, onRequestSort } = props;
  const createSortHandler = (property) => (event) => {
    onRequestSort(event, property);
  };

  return (
    <TableHead className={classes.tableHead}>
      <TableRow>
        {headCells.map((headCell) => (
          <TableCell
            key={headCell.id}
            style={"width" in headCell ? { width: headCell.width } : {}}
            align={headCell.align || "left"}
            padding={headCell.disablePadding ? "none" : "normal"}
            sortDirection={orderBy === headCell.id ? order : false}
          >
            <TableSortLabel
              active={orderBy === headCell.id}
              style={{
                ...("align" in headCell ? { textAlign: headCell.align } : {}),
                gap: 8,
              }}
              direction={orderBy === headCell.id ? order : "asc"}
              onClick={createSortHandler(headCell.id)}
            >
              <span
                style={
                  "align" in headCell && headCell.align === "center"
                    ? { position: "relative", left: 10 }
                    : {}
                }
              >
                {headCell.label}
              </span>
              {orderBy === headCell.id ? (
                <span className={classes.visuallyHidden}>
                  {order === "desc" ? "sorted descending" : "sorted ascending"}
                </span>
              ) : null}
            </TableSortLabel>
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}

EnhancedTableHead.propTypes = {
  classes: PropTypes.object.isRequired,
  onRequestSort: PropTypes.func.isRequired,
  order: PropTypes.oneOf(["asc", "desc"]).isRequired,
  orderBy: PropTypes.string.isRequired,
};

const useStyles = makeStyles((theme) => ({
  root: {
    width: "100%",
    boxSizing: "border-box",
  },
  paper: {
    width: "100%",
    marginBottom: theme.spacing(2),
    borderRadius: 12,
    overflow: "hidden",
  },
  table: {
    minWidth: 750,
  },
  tableContainer: {
    borderRadius: 12,
    maxHeight: "none",
    height: "auto",
  },
  tableHead: {
    height: "4rem",
    background: "linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)",
    "& th": {
      color: "#ffffff",
      fontWeight: 525,
      letterSpacing: "0.02em",
    },
    "& .MuiTableSortLabel-root": {
      color: "#ffffff",
      "&:hover": {
        color: "#ffffff",
      },
      "&.MuiTableSortLabel-active": {
        color: "#ffffff",
      },
    },
    "& .MuiTableSortLabel-icon": {
      color: "#ffffff !important",
    },
  },
  profileLinkGroup: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 6,
  },
  profileLinkButton: {
    display: "inline-flex",
    lineHeight: 0,
  },
  steamIcon: {
    height: "2.4rem",
  },
  externalProfileIcon: {
    height: "1.7rem",
    width: "1.7rem",
    display: "block",
    objectFit: "cover",
    borderRadius: 6,
    background: theme.palette.background.paper,
    border: `solid 1px ${theme.palette.divider}`,
  },
  faceitIcon: {
    height: "1.6rem",
    width: "1.6rem",
    background: theme.palette.background.paper,
    padding: 5,
    borderRadius: 8,
    border: `solid 1px ${theme.palette.divider}`,
  },
  phoneVerificationIdle: {
    minWidth: 96,
    fontWeight: 600,
    color: theme.palette.text.secondary,
    backgroundColor: theme.palette.action.hover,
    border: `1px solid ${theme.palette.divider}`,
  },
  phoneVerificationPending: {
    minWidth: 96,
    fontWeight: 600,
    color: "#ffffff",
    backgroundColor: "#1565c0",
  },
  phoneVerificationVerified: {
    minWidth: 96,
    fontWeight: 600,
    color: "#ffffff",
    backgroundColor: "#2e7d32",
  },
  phoneVerificationExpired: {
    minWidth: 96,
    fontWeight: 600,
    color: "#ffffff",
    backgroundColor: "#6d4c41",
  },
  verificationChipStack: {
    display: "inline-flex",
    flexDirection: "column",
    alignItems: "stretch",
    gap: theme.spacing(0.5),
  },
  statusChipStack: {
    display: "inline-flex",
    flexDirection: "column",
    alignItems: "center",
    gap: theme.spacing(0.75),
    padding: theme.spacing(0.75, 0),
  },
  verificationChipIcon: {
    fontSize: "1rem",
    color: "inherit",
  },
  phonePresenceIcon: {
    fontSize: "1.2rem",
  },
  phonePresenceIconPresent: {
    color: theme.palette.success.main,
  },
  phonePresenceIconMissing: {
    color: theme.palette.text.disabled,
  },
  playerNameCellPrimary: {
    display: "block",
    lineHeight: 1.35,
  },
  playerNameCellSecondary: {
    display: "block",
    marginTop: theme.spacing(0.25),
    color: theme.palette.text.secondary,
    fontSize: "0.75rem",
    lineHeight: 1.35,
  },
  syntheticEmailLabel: {
    display: "block",
    marginTop: theme.spacing(0.25),
    color: theme.palette.text.disabled,
    fontStyle: "italic",
    fontSize: "0.75rem",
    lineHeight: 1.35,
  },
  activityChipStack: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    gap: theme.spacing(0.375),
    marginTop: theme.spacing(0.5),
    maxWidth: "100%",
  },
  joinedAtChip: {
    height: 18,
    fontWeight: 600,
    fontSize: "0.65rem",
    color: theme.palette.text.secondary,
    backgroundColor: theme.palette.action.hover,
    border: `1px solid ${theme.palette.divider}`,
    "&.MuiChip-sizeSmall": {
      height: 18,
    },
    "& .MuiChip-label": {
      paddingLeft: 6,
      paddingRight: 6,
    },
  },
  rejected: {
    background: "linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)",
    color: "#ffffff",
    fontWeight: 600,
    fontSize: "0.75rem",
    letterSpacing: "0.05em",
    borderRadius: 8,
    padding: "6px 12px",
    boxShadow: "0 2px 4px rgba(220, 38, 38, 0.25)",
    textTransform: "uppercase",
  },
  pending: {
    background: "linear-gradient(135deg, #64748b 0%, #475569 100%)",
    color: "#ffffff",
    fontWeight: 600,
    fontSize: "0.75rem",
    letterSpacing: "0.05em",
    borderRadius: 8,
    padding: "6px 12px",
    boxShadow: "0 2px 4px rgba(100, 116, 139, 0.25)",
    textTransform: "uppercase",
  },
  pending_review: {
    background: "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
    color: "#ffffff",
    fontWeight: 600,
    fontSize: "0.75rem",
    letterSpacing: "0.05em",
    borderRadius: 8,
    padding: "6px 12px",
    boxShadow: "0 2px 4px rgba(245, 158, 11, 0.35)",
    textTransform: "uppercase",
  },
  verified: {
    background: "linear-gradient(135deg, #22c55e 0%, #16a34a 100%)",
    color: "#ffffff",
    fontWeight: 600,
    fontSize: "0.75rem",
    letterSpacing: "0.05em",
    borderRadius: 8,
    padding: "6px 12px",
    boxShadow: "0 2px 4px rgba(34, 197, 94, 0.25)",
    textTransform: "uppercase",
  },
  active: {
    background: "linear-gradient(135deg, #00e686 0%, #10b981 100%)",
    color: "#ffffff",
    fontWeight: 600,
    fontSize: "0.75rem",
    letterSpacing: "0.05em",
    borderRadius: 8,
    padding: "6px 12px",
    boxShadow: "0 2px 4px rgba(59, 130, 246, 0.25)",
    textTransform: "uppercase",
  },
  noteligible: {
    background: "linear-gradient(135deg, #a855f7 0%, #9333ea 100%)",
    color: "#ffffff",
    fontWeight: 600,
    fontSize: "0.75rem",
    letterSpacing: "0.05em",
    borderRadius: 8,
    padding: "6px 12px",
    boxShadow: "0 2px 4px rgba(168, 85, 247, 0.25)",
    textTransform: "uppercase",
  },
  banned: {
    background: "linear-gradient(135deg, #1f2937 0%, #111827 100%)",
    color: "#ffffff",
    fontWeight: 600,
    fontSize: "0.75rem",
    letterSpacing: "0.05em",
    borderRadius: 8,
    padding: "6px 12px",
    boxShadow: "0 2px 4px rgba(0, 0, 0, 0.3)",
    textTransform: "uppercase",
  },
  membershipFree: {
    minWidth: 64,
    fontWeight: 600,
    color: theme.palette.text.secondary,
    backgroundColor: theme.palette.action.hover,
    border: `1px solid ${theme.palette.divider}`,
  },
  membershipPremium: {
    minWidth: 80,
    fontWeight: 600,
    color: "#1565c0",
    backgroundColor: "#e3f2fd",
    border: "1px solid #bbdefb",
  },
  membershipPro: {
    minWidth: 64,
    fontWeight: 600,
    color: "#6a1b9a",
    backgroundColor: "#f3e5f5",
    border: "1px solid #e1bee7",
  },
  membershipUnknown: {
    minWidth: 80,
    fontWeight: 600,
    color: theme.palette.text.secondary,
    backgroundColor: theme.palette.action.selected,
    border: `1px solid ${theme.palette.divider}`,
  },
  identityRiskHigh: {
    minWidth: 76,
    fontWeight: 600,
    color: "#ffffff",
    backgroundColor: "#c62828",
  },
  identityRiskMedium: {
    minWidth: 76,
    fontWeight: 600,
    color: "#ffffff",
    backgroundColor: "#ef6c00",
  },
  identityRiskPending: {
    minWidth: 76,
    fontWeight: 600,
    color: "#ffffff",
    backgroundColor: "#1565c0",
  },
  identityRiskFailed: {
    minWidth: 76,
    fontWeight: 600,
    color: "#ffffff",
    backgroundColor: "#6d4c41",
  },
  identityRiskClear: {
    minWidth: 76,
    fontWeight: 600,
    color: theme.palette.text.secondary,
    backgroundColor: theme.palette.action.hover,
    border: `1px solid ${theme.palette.divider}`,
  },
  emptyStateCell: {
    padding: theme.spacing(5, 2),
  },
  emptyStateTitle: {
    fontWeight: 600,
    color: theme.palette.text.primary,
  },
  emptyStateText: {
    marginTop: theme.spacing(1),
    color: theme.palette.text.secondary,
  },
  emptyStateButton: {
    marginTop: theme.spacing(2),
  },
  visuallyHidden: {
    border: 0,
    clip: "rect(0 0 0 0)",
    height: 1,
    margin: -1,
    overflow: "hidden",
    padding: 0,
    position: "absolute",
    top: 20,
    width: 1,
  },
  menuPaper: {
    minWidth: 240,
    borderRadius: 8,
    boxShadow: "0 4px 20px rgba(0, 0, 0, 0.15)",
    border: `1px solid ${theme.palette.divider}`,
  },
  menuItem: {
    padding: "12px 16px",
    fontSize: "14px",
    fontWeight: 500,
    borderBottom: `1px solid ${theme.palette.divider}`,
    "&:last-child": {
      borderBottom: "none",
    },
    "&:hover": {
      backgroundColor: theme.palette.action.hover,
    },
  },
}));

export default function EnhancedTable({
  rows,
  user,
  initialFilters = null,
  initialPage = 0,
  initialOrder = "desc",
  initialOrderBy = "name",
  canManageAdvancedUserTools = false,
  openSetEloDialog,
  openRebuildPlayerEloDialog,
  openSetAsVerifiedDialog,
  openStartPhoneVerificationDialog,
  openSetAsNotEligibleDialog,
  openEligibilityThreadDialog,
  openSetAsTrackerStatsDialog,
  openSetAsBannedDialog,
  openSetMembershipGrantDialog,
  openSetMembershipRevokeDialog,
  openFounderBadgeDialog,
  openIdentityReviewDialog,
  openReputationOutcomesPage,
  openSendApplicationFeedbackDialog,
  openReadinessReviewDialog,
  openDeleteUserDialog = () => {},
}) {
  const classes = useStyles();
  const [order, setOrder] = React.useState(initialOrder);
  const [orderBy, setOrderBy] = React.useState(initialOrderBy);
  const [page, setPage] = React.useState(initialPage);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);
  const [filters, setFilters] = React.useState(() => ({
    ...DEFAULT_USER_MANAGEMENT_FILTERS,
    ...(initialFilters || {}),
  }));
  const [menuAnchor, setMenuAnchor] = React.useState(null);
  const [activeUserContext, setActiveUserContext] = React.useState({});
  const activeUserSteamId = resolveUserSteamId(activeUserContext.row);

  const handleMenuOpen = (event, row) => {
    setActiveUserContext({ userId: row.userId, row });
    setMenuAnchor(event.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
  };

  const adjustElo = ({ userId, row }) => {
    openSetEloDialog({ userId, row });
    handleMenuClose();
  };

  const rebuildEloFromMatchHistory = ({ userId, row }) => {
    openRebuildPlayerEloDialog({ userId, row });
    handleMenuClose();
  };

  const setAsVerified = ({ userId, row }) => {
    openSetAsVerifiedDialog({ userId, row });
    handleMenuClose();
  };

  const startPhoneVerification = ({ userId, row }) => {
    openStartPhoneVerificationDialog({ userId, row });
    handleMenuClose();
  };

  const setAsNotEligible = ({ userId, row }) => {
    openSetAsNotEligibleDialog({ userId, row });
    handleMenuClose();
  };

  const openEligibilityThread = ({ userId, row }) => {
    openEligibilityThreadDialog({ userId, row });
    handleMenuClose();
  };

  const setAsBanned = ({ userId, row }) => {
    openSetAsBannedDialog({ userId, row });
    handleMenuClose();
  };

  const setAsTrackerStats = ({ userId, row }) => {
    openSetAsTrackerStatsDialog({ userId, row });
    handleMenuClose();
  };

  const grantMembership = ({ userId, row }) => {
    openSetMembershipGrantDialog({ userId, row });
    handleMenuClose();
  };

  const revokeMembership = ({ userId, row }) => {
    openSetMembershipRevokeDialog({ userId, row });
    handleMenuClose();
  };

  const manageFounderBadge = ({ userId, row }) => {
    openFounderBadgeDialog({ userId, row });
    handleMenuClose();
  };

  const openIdentityReview = ({ userId, row }) => {
    openIdentityReviewDialog({ userId, row });
    handleMenuClose();
  };

  const openReputationOutcomes = ({ userId, row }) => {
    openReputationOutcomesPage({ userId, row });
    handleMenuClose();
  };

  const sendApplicationFeedback = ({ userId, row }) => {
    openSendApplicationFeedbackDialog({ userId, row });
    handleMenuClose();
  };

  const openReadinessReview = ({ userId, row }) => {
    openReadinessReviewDialog({ userId, row });
    handleMenuClose();
  };

  const deleteUser = ({ userId, row }) => {
    openDeleteUserDialog({ userId, row });
    handleMenuClose();
  };

  const copySteamId = async () => {
    if (!activeUserSteamId) {
      handleMenuClose();
      return;
    }

    try {
      if (typeof navigator !== "undefined" && navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(activeUserSteamId);
      }
    } catch (error) {
      console.error("Failed to copy Steam ID", error);
    }

    handleMenuClose();
  };

  const highlightText = (text, searchTerm) => {
    if (!text) return text;
    const normalizedSearchTerm = String(searchTerm || "").trim();
    if (!normalizedSearchTerm) return text;

    const escapedSearchTerm = normalizedSearchTerm.replace(
      /[.*+?^${}()|[\]\\]/g,
      "\\$&"
    );
    const parts = String(text).split(
      new RegExp(`(${escapedSearchTerm})`, "gi")
    );

    return parts.map((part, index) =>
      index % 2 === 1 ? (
        <mark
          key={index}
          style={{
            backgroundColor: "#ffeb3b",
            padding: "2px 4px",
            borderRadius: "3px",
          }}
        >
          {part}
        </mark>
      ) : part
    );
  };

  const updateFilters = React.useCallback((partialFilters) => {
    setFilters((currentFilters) => ({
      ...currentFilters,
      ...partialFilters,
    }));
    setPage(0);
  }, []);

  const handleResetFilters = React.useCallback(() => {
    setFilters({ ...DEFAULT_USER_MANAGEMENT_FILTERS });
    setPage(0);
  }, []);

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };



  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const filteredRows = React.useMemo(
    () =>
      Array.isArray(rows)
        ? rows.filter((row) => rowMatchesFilters(row, filters))
        : [],
    [filters, rows]
  );
  const totalRowCount = Array.isArray(rows) ? rows.length : 0;
  const clampedPage = getClampedPage(page, filteredRows.length, rowsPerPage);

  React.useEffect(() => {
    if (page !== clampedPage) {
      setPage(clampedPage);
    }
  }, [page, clampedPage]);

  const emptyRows =
    rowsPerPage -
    Math.min(rowsPerPage, filteredRows.length - clampedPage * rowsPerPage);

  return (
    <div className={classes.root}>
      <UserManagementFilterBar
        filters={filters}
        totalCount={totalRowCount}
        filteredCount={filteredRows.length}
        onChange={updateFilters}
        onReset={handleResetFilters}
      />
      <Paper className={classes.paper}>
        <TableContainer className={classes.tableContainer}>
          <Table
            className={classes.table}
            aria-labelledby="tableTitle"
            size="medium"
            aria-label="enhanced table"
          >
            <EnhancedTableHead
              classes={classes}
              order={order}
              orderBy={orderBy}
              onRequestSort={handleRequestSort}
            />
            <TableBody>
              {filteredRows.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={headCells.length}
                    align="center"
                    className={classes.emptyStateCell}
                  >
                    <Typography variant="h6" className={classes.emptyStateTitle}>
                      {totalRowCount === 0
                        ? "No users are available right now"
                        : "No users match the current filters"}
                    </Typography>
                    <Typography variant="body2" className={classes.emptyStateText}>
                      {totalRowCount === 0
                        ? "Check back after users start signing up."
                        : "Adjust the toolbar filters or clear them to show the full table again."}
                    </Typography>
                    {totalRowCount > 0 ? (
                      <Button
                        className={classes.emptyStateButton}
                        color="primary"
                        onClick={handleResetFilters}
                      >
                        Clear filters
                      </Button>
                    ) : null}
                  </TableCell>
                </TableRow>
              ) : (
                stableSort(filteredRows, getComparator(order, orderBy))
                  .slice(
                    clampedPage * rowsPerPage,
                    clampedPage * rowsPerPage + rowsPerPage
                  )
                  .map((row, index) => {
                    const labelId = `enhanced-table-checkbox-${index}`;
                    const membershipDisplay = getMembershipTierDisplay(row);
                    const joinedAtMillis = getJoinedAtMillis(row);
                    const lastVisitedAtMillis = getDateMillis(row?.lastVisitedAt);
                    const lastLoginAtMillis = getDateMillis(row?.lastLoginAt);
                    const hasProvidedPhone = hasProvidedPhoneNumber(row);

                    let displayAccountStatus = (row.accountStatus || "pending").toUpperCase();
                    if (displayAccountStatus === "NOTELIGIBLE") {
                      displayAccountStatus = "NOT ELIGIBLE";
                    }

                    const emailDisplay = formatEmailDisplay(row.email);
                    const csrepUrl = buildCsrepUrl(row);
                    const vacBanUrl = buildVacBanUrl(row);

                    return (
                      <TableRow
                        hover
                        key={row.userId}
                      >
                        <TableCell component="th" id={labelId} scope="row">
                          <Typography
                            variant="body2"
                            className={classes.playerNameCellPrimary}
                          >
                            {highlightText(row.name, filters.search)}
                          </Typography>
                          {emailDisplay.isSynthetic ? (
                            <Tooltip arrow title={row.email} placement="top">
                              <Typography
                                variant="body2"
                                className={classes.syntheticEmailLabel}
                              >
                                {emailDisplay.label}
                              </Typography>
                            </Tooltip>
                          ) : (
                            <Typography
                              variant="body2"
                              className={classes.playerNameCellSecondary}
                            >
                              {highlightText(row.email, filters.search)}
                            </Typography>
                          )}
                          <div className={classes.activityChipStack}>
                            <Tooltip
                              arrow
                              title={formatAbsoluteTime(
                                joinedAtMillis,
                                "Join date unavailable"
                              )}
                              placement="top"
                            >
                              <Chip
                                size="small"
                                className={classes.joinedAtChip}
                                label={`Joined ${formatRelativeTime(
                                  joinedAtMillis,
                                  "unknown"
                                )}`}
                              />
                            </Tooltip>
                            <Tooltip
                              arrow
                              title={formatAbsoluteTime(
                                lastVisitedAtMillis,
                                "Last visit unknown"
                              )}
                              placement="top"
                            >
                              <Chip
                                size="small"
                                className={classes.joinedAtChip}
                                label={`Visited ${formatRelativeTime(
                                  lastVisitedAtMillis,
                                  "never"
                                )}`}
                              />
                            </Tooltip>
                            <Tooltip
                              arrow
                              title={formatAbsoluteTime(
                                lastLoginAtMillis,
                                "Last login unknown"
                              )}
                              placement="top"
                            >
                              <Chip
                                size="small"
                                className={classes.joinedAtChip}
                                label={`Login ${formatRelativeTime(
                                  lastLoginAtMillis,
                                  "never"
                                )}`}
                              />
                            </Tooltip>
                          </div>
                        </TableCell>
                        <TableCell padding="none">{highlightText(row.nickname, filters.search)}</TableCell>
                        <TableCell padding="none" align="center">
                          <div className={classes.statusChipStack}>
                            <Chip
                              className={classes[row.accountStatus || "pending"]}
                              label={displayAccountStatus}
                            />
                            <Chip
                              size="small"
                              className={getMembershipChipClass(classes, membershipDisplay.tier)}
                              label={membershipDisplay.label}
                            />
                            <Chip
                              size="small"
                              className={getIdentityRiskChipClass(
                                classes,
                                row.identityRisk
                              )}
                              label={getIdentityRiskLabel(row.identityRisk)}
                            />
                          </div>
                        </TableCell>
                        <TableCell padding="none" align="center">
                          {row.elo ?? "1000"}
                        </TableCell>
                        <TableCell padding="none" align="center">
                          <UserCountryCell row={row} />
                        </TableCell>
                        <TableCell padding="none" align="center">
                          {row.age}
                        </TableCell>
                        <TableCell align="center">
                          <div className={classes.profileLinkGroup}>
                            <ProfileIconLink
                              href={row.steamProfileUrl || null}
                              src="https://ouro-public-assets.s3.eu-west-2.amazonaws.com/steam-icon-14889.png"
                              label="Steam profile"
                              iconClassName={classes.steamIcon}
                              linkClassName={classes.profileLinkButton}
                            />
                            {csrepUrl ? (
                              <ProfileIconLink
                                href={csrepUrl}
                                src={CSREP_ICON_URL}
                                label="CSRep"
                                iconClassName={classes.externalProfileIcon}
                                linkClassName={classes.profileLinkButton}
                              />
                            ) : null}
                            {vacBanUrl ? (
                              <ProfileIconLink
                                href={vacBanUrl}
                                src={VAC_BAN_ICON_URL}
                                label="VAC-ban"
                                iconClassName={classes.externalProfileIcon}
                                linkClassName={classes.profileLinkButton}
                              />
                            ) : null}
                          </div>
                        </TableCell>
                        <TableCell align="center">
                          {row.faceitProfileUrl ? (
                            <a
                              className={classes.faceitProfileLinkButton}
                              href={row.faceitProfileUrl}
                              target="_blank"
                              rel="noreferrer"
                            >
                              <img
                                className={classes.faceitIcon}
                                src="https://ouro-public-assets.s3.eu-west-2.amazonaws.com/faceit-seeklogo.svg"
                                alt="FaceIT profile"
                              />
                            </a>
                          ) : (
                            <img
                              className={classes.faceitIcon}
                              src="https://ouro-public-assets.s3.eu-west-2.amazonaws.com/faceit-seeklogo.svg"
                              alt="FaceIT profile — not linked"
                              style={{ opacity: 0.25, filter: "grayscale(100%)", cursor: "default" }}
                            />
                          )}
                        </TableCell>
                        <TableCell padding="none" align="center">
                          <Tooltip
                            arrow
                            title={
                              hasProvidedPhone
                                ? "Phone number provided"
                                : "Phone number missing"
                            }
                            placement="top"
                          >
                            <PhoneIcon
                              className={`${classes.phonePresenceIcon} ${
                                hasProvidedPhone
                                  ? classes.phonePresenceIconPresent
                                  : classes.phonePresenceIconMissing
                              }`}
                            />
                          </Tooltip>
                        </TableCell>
                        <TableCell padding="none" align="center">
                          <div className={classes.verificationChipStack}>
                            <Chip
                              size="small"
                              icon={<PhoneIcon className={classes.verificationChipIcon} />}
                              className={getPhoneVerificationColumnChipClass(
                                classes,
                                row
                              )}
                              label={getPhoneVerificationColumnLabel(row)}
                              title={`Phone verification: ${getPhoneVerificationColumnLabel(
                                row
                              )}`}
                            />
                            <Chip
                              size="small"
                              icon={
                                <FingerprintIcon
                                  className={classes.verificationChipIcon}
                                />
                              }
                              className={getBiometricVerificationColumnChipClass(
                                classes,
                                row
                              )}
                              label={getBiometricVerificationColumnLabel(row)}
                              title={`Biometric verification: ${getBiometricVerificationColumnLabel(
                                row
                              )}`}
                            />
                            <Chip
                              size="small"
                              icon={
                                <NotificationsActiveIcon
                                  className={classes.verificationChipIcon}
                                />
                              }
                              className={getDesktopNotificationColumnChipClass(
                                classes,
                                row
                              )}
                              label={getDesktopNotificationColumnLabel(row)}
                              title={`Desktop notifications: ${getDesktopNotificationColumnLabel(
                                row
                              )}`}
                            />
                          </div>
                        </TableCell>
                        <TableCell padding="none" align="center">
                          <Tooltip
                            arrow
                            title={`Onboarding tier ${row.completedOnboardingTier ?? 0}; ${row.profileTrustTier || "not computed"}`}
                          >
                            <Chip
                              size="small"
                              className={classes.phoneVerificationIdle}
                              label={`TF ${row.profileTrustFactor != null ? Number(row.profileTrustFactor).toFixed(2) : "—"}/10`}
                            />
                          </Tooltip>
                        </TableCell>
                        <TableCell align="center">
                          <IconButton
                            className={classes.menuButton}
                            aria-haspopup="true"
                            onClick={(e) => handleMenuOpen(e, row)}
                          >
                            <MoreVertIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    );
                  })
              )}
              {emptyRows > 0 && filteredRows.length > 0 && filteredRows.length >= rowsPerPage && (
                <TableRow style={{ height: 40 * emptyRows }}>
                  <TableCell colSpan={headCells.length} />
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={filteredRows.length}
          rowsPerPage={rowsPerPage}
          page={clampedPage}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>

      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
        getContentAnchorEl={null}
        anchorOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        PaperProps={{
          className: classes.menuPaper,
        }}
      >
        {canManageAdvancedUserTools && (
          <MenuItem
            onClick={() => adjustElo(activeUserContext)}
            className={classes.menuItem}
          >
            Set ELO Rating
          </MenuItem>
        )}

        {canManageAdvancedUserTools && (
          <MenuItem
            onClick={() => rebuildEloFromMatchHistory(activeUserContext)}
            disabled={!resolveUserSteamId(activeUserContext.row)}
            className={classes.menuItem}
          >
            Rebuild ELO From Match History
          </MenuItem>
        )}

        <MenuItem
          onClick={copySteamId}
          disabled={!activeUserSteamId}
          className={classes.menuItem}
        >
          Copy Steam ID
        </MenuItem>

        <MenuItem
          onClick={() => openReputationOutcomes(activeUserContext)}
          disabled={!activeUserSteamId}
          className={classes.menuItem}
        >
          Review Leaver Outcomes
        </MenuItem>

        <MenuItem
          onClick={() => openIdentityReview(activeUserContext)}
          className={classes.menuItem}
        >
          Open Identity Review
        </MenuItem>

        <MenuItem
          onClick={() => openReadinessReview(activeUserContext)}
          className={classes.menuItem}
        >
          Review Onboarding Checklist
        </MenuItem>

        <MenuItem
          onClick={() =>
            sendApplicationFeedback(activeUserContext)
          }
          disabled={
            !activeUserContext.row ||
            !["pending", "pending_review"].includes(
              activeUserContext.row.accountStatus
            )
          }
          className={classes.menuItem}
        >
          Send Application Feedback
        </MenuItem>

        <MenuItem
          onClick={() => setAsVerified(activeUserContext)}
          disabled={isSetAsVerifiedDisabled(activeUserContext.row)}
          className={classes.menuItem}
        >
          Set as Verified
        </MenuItem>

        <MenuItem
          onClick={() => startPhoneVerification(activeUserContext)}
          disabled={isApprovePhoneVerificationDisabled(activeUserContext.row)}
          className={classes.menuItem}
        >
          Approve Phone Verification
        </MenuItem>

        {canManageAdvancedUserTools && (
          <MenuItem
            onClick={() => setAsTrackerStats(activeUserContext)}
            className={classes.menuItem}
          >
            Update Tracker Stats
          </MenuItem>
        )}

        <MenuItem
          onClick={() => grantMembership(activeUserContext)}
          className={classes.menuItem}
        >
          Grant Membership
        </MenuItem>

        <MenuItem
          onClick={() => revokeMembership(activeUserContext)}
          disabled={
            !activeUserContext.row?.membership?.grantedByAdmin ||
            !activeUserContext.row?.membership?.tier ||
            activeUserContext.row?.membership?.tier === "free"
          }
          className={classes.menuItem}
        >
          Revoke Free Grant
        </MenuItem>

        <MenuItem
          onClick={() => manageFounderBadge(activeUserContext)}
          className={classes.menuItem}
        >
          Founder Badge
        </MenuItem>

        <MenuItem
          onClick={() => setAsNotEligible(activeUserContext)}
          className={classes.menuItem}
        >
          Set as Not Eligible
        </MenuItem>

        {activeUserContext?.row?.accountStatus === "noteligible" && (
          <MenuItem
            onClick={() => openEligibilityThread(activeUserContext)}
            className={classes.menuItem}
          >
            View Eligibility Thread
          </MenuItem>
        )}

        <MenuItem
          onClick={() => setAsBanned(activeUserContext)}
          className={classes.menuItem}
        >
          Temporary Ban
        </MenuItem>

        {canManageAdvancedUserTools && (
          <MenuItem
            onClick={() => deleteUser(activeUserContext)}
            disabled={
              !activeUserContext.row ||
              activeUserContext.row.userId === user?.userId ||
              activeUserContext.row.isOuroAdmin
            }
            className={classes.menuItem}
            style={{ color: "#d32f2f" }}
          >
            Delete User
          </MenuItem>
        )}
      </Menu>
    </div>
  );
}
